// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  fmtDateTime,
  apiFetchInit,
  HR_REATTEMPT_REASON_LABELS,
} from "@/legacy/helpersModule";
import { Badge } from "@/shared/components/ui/Badge";
import { Button, Field, Select, Textarea, useToast } from "@/shared/components/ui";
import { Modal } from "@/shared/components/ui/Modal";
import { useEscapeKey } from "@/shared/hooks/useEscapeKey";
import { HrNotificationEmailBanner } from "@/features/hr/components/HrNotificationEmailBanner";

export function ReattemptQueue({ onBack, onResolved }) {
  const toast = useToast();
  useEscapeKey(onBack);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [decision, setDecision] = useState("approve");
  const [hrCode, setHrCode] = useState("QUALITY_IMPACTED");
  const [hrNotes, setHrNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => {
    setLoading(true);
    fetch("/api/admin/reattempt-pending", apiFetchInit())
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => setItems(d.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const onRealtime = () => load();
    window.addEventListener("swarhr:realtime", onRealtime);
    return () => window.removeEventListener("swarhr:realtime", onRealtime);
  }, []);
  const submit = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      const r = await fetch(
        `/api/admin/applications/${sel.applicationId}/reattempt-resolve`,
        apiFetchInit({
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ decision, hrReasonCode: hrCode, hrNotes: hrNotes.trim() }),
        }),
      );
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.show({ tone: "danger", title: "Request failed", message: e.error || "Could not resolve the reattempt request." });
      } else {
        setSel(null);
        setHrNotes("");
        load();
        if (typeof onResolved === "function") await onResolved();
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-dvh safe-bottom bg-surface-alt">
      <div className="bg-white border-b border-line text-ink-900 px-4 sm:px-6 py-3 sm:py-4 safe-top safe-x flex items-center gap-3">
        <button type="button" onClick={onBack} className="text-ink-400 hover:text-ink-900 text-sm">← Back</button>
        <span className="font-bold">Reattempt approvals</span>
        <Badge />
      </div>
      <HrNotificationEmailBanner requestType="reattempt" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 safe-x">
        {loading ? <p className="text-ink-400 text-sm">Loading…</p> : items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-line p-8 text-center text-ink-400 text-sm">No pending reattempt requests.</div>
        ) : (
          <div className="space-y-3">
            {items.map((row) => (
              <button
                key={row.applicationId}
                type="button"
                onClick={() => { setSel(row); setDecision("approve"); setHrCode("QUALITY_IMPACTED"); }}
                className="w-full text-left bg-white rounded-2xl border border-line p-4 hover:border-brand-300 transition-colors"
              >
                <p className="font-bold text-ink-900">{row.candidateName}</p>
                <p className="text-xs text-ink-400">{row.candidateEmail}</p>
                <p className="text-sm text-brand-700 font-semibold mt-1">{row.jobTitle || "—"} · Application #{row.applicationId}</p>
                <p className="text-xs text-ink-400 mt-1">Requested: {row.requestedAt ? fmtDateTime(row.requestedAt) : "—"}</p>
                {row.candidateReasonCode && <p className="text-xs text-ink-600 mt-2">Candidate: {row.candidateReasonCode}{row.candidateReasonText ? ` — ${row.candidateReasonText.slice(0, 200)}` : ""}</p>}
              </button>
            ))}
          </div>
        )}
        {sel && (
          <Modal
            title="Resolve reattempt"
            onClose={busy ? undefined : () => setSel(null)}
          >
            <p className="text-sm text-ink-600 mb-4">{sel.candidateName} · {sel.jobTitle}</p>
            {sel.candidateReasonCode ? (
              <div className="bg-surface-alt border border-line rounded-xl p-3 mb-4">
                <p className="text-xs font-bold text-ink-400 uppercase mb-1.5">Candidate request</p>
                <p className="text-sm text-ink-900 leading-snug">
                  Candidate: {sel.candidateReasonCode}
                  {sel.candidateReasonText ? ` — ${String(sel.candidateReasonText).trim()}` : ""}
                </p>
              </div>
            ) : null}
            <div className="space-y-3 mb-4">
              <Field label="Decision">
                <Select value={decision} onChange={(e) => setDecision(e.target.value)}>
                  <option value="approve">Approve (clears prior answers & transcript for this application)</option>
                  <option value="reject">Reject</option>
                </Select>
              </Field>
              <Field label="HR reason" required>
                <Select value={hrCode} onChange={(e) => setHrCode(e.target.value)}>
                  {Object.keys(HR_REATTEMPT_REASON_LABELS).map((k) => <option key={k} value={k}>{HR_REATTEMPT_REASON_LABELS[k]}</option>)}
                </Select>
              </Field>
              <Field label="Notes (optional)">
                <Textarea value={hrNotes} onChange={(e) => setHrNotes(e.target.value)} rows={3} placeholder="Internal notes…" />
              </Field>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button type="button" variant="secondary" disabled={busy} onClick={() => setSel(null)} className="w-full sm:flex-1">Cancel</Button>
              <Button type="button" loading={busy} onClick={submit} className="w-full sm:flex-1">Submit</Button>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
}

